import { InputNumber, message, Switch } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { authStore } from '@/stores';
import type {
  InviteApplicationRow,
  InviteFollowupRow,
} from '@/stores/invite-admin-store';
import { inviteAdminStore } from '@/stores/inviteAdminStore';

function timestamp(value: number) {
  return new Date(value * 1000).toLocaleString('zh-CN');
}

export const ImmediateInviteRecords = observer(() => {
  const policy = inviteAdminStore.policy;
  const [day, setDay] = useState(50),
    [week, setWeek] = useState(350),
    [month, setMonth] = useState(1500);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    setDay(policy?.day ?? 50);
    setWeek(policy?.week ?? 350);
    setMonth(policy?.month ?? 1500);
    setEnabled(policy?.enabled ?? false);
  }, [policy]);
  const applications: Column<InviteApplicationRow>[] = [
    {
      id: 'email',
      header: '申请邮箱',
      accessor: (row) => row.questionnaire.email,
    },
    {
      id: 'answers',
      header: '问卷',
      accessor: (row) => (
        <details>
          <summary className="cursor-pointer">查看五题</summary>
          <dl className="space-y-1 mt-2">
            <dt>职业</dt>
            <dd>{row.questionnaire.role}</dd>
            <dt>来源</dt>
            <dd>{row.questionnaire.source}</dd>
            <dt>使用场景</dt>
            <dd>{row.questionnaire.useCases.join('、')}</dd>
            <dt>团队人数</dt>
            <dd>{row.questionnaire.teamSize}</dd>
          </dl>
        </details>
      ),
    },
    {
      id: 'channel',
      header: '渠道归因',
      accessor: (row) => row.questionnaire.attribution.channel || '直接申请',
    },
    {
      id: 'issued',
      header: '发码时间',
      accessor: (row) => timestamp(row.issuedAt),
    },
    {
      id: 'state',
      header: '兑换状态',
      accessor: (row) =>
        row.state === 'ready'
          ? '待兑换'
          : row.state === 'redeemed'
            ? '已兑换'
            : '不可用',
    },
  ];
  const followups: Column<InviteFollowupRow>[] = [
    { id: 'email', header: '申请邮箱', accessor: (row) => row.email },
    {
      id: 'contact',
      header: '自愿回访联系方式',
      accessor: (row) =>
        `${row.contact.kind === 'wechat' ? '微信号' : '群昵称'}：${row.contact.value}`,
    },
    {
      id: 'community',
      header: '群名',
      accessor: (row) => row.contact.community || '-',
    },
    {
      id: 'consent',
      header: '同意时间',
      accessor: (row) => timestamp(row.consentAt),
    },
  ];
  const statistics = inviteAdminStore.statistics;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          ['成功发码', statistics?.issued],
          ['已兑换邀请码', statistics?.used],
          ['当前有效激活账号', statistics?.activated],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-lg border p-4">
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold">{value ?? '-'}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border p-4 space-y-3">
        <h2 className="font-medium">个人发码额度</h2>
        <p className="text-sm text-muted-foreground">
          按成功发码计数，以北京时间自然日、周、月为周期。任一额度达到上限即停止新发码，已领码仍可激活。
          {policy === null && '尚未设置额度，新发码关闭。'}
        </p>
        <div className="flex flex-wrap gap-4 items-end">
          {(
            [
              ['每日', day, setDay],
              ['每周', week, setWeek],
              ['每月', month, setMonth],
            ] as const
          ).map(([label, value, setValue]) => (
            <label
              htmlFor={`invite-limit-${label}`}
              key={label}
              className="flex flex-col gap-1 text-sm"
            >
              {label}
              <InputNumber
                id={`invite-limit-${label}`}
                aria-label={`${label}发码额度`}
                disabled={
                  !authStore.can(ADMIN_PERMISSIONS.invitesWrite) ||
                  inviteAdminStore.busy
                }
                min={0}
                max={1000000}
                precision={0}
                value={value}
                onChange={(v) => {
                  if (v !== null) setValue(v);
                }}
              />
            </label>
          ))}
          <label
            htmlFor="personal-invite-enabled"
            className="flex gap-2 items-center text-sm pb-1"
          >
            允许新发码
            <Switch
              id="personal-invite-enabled"
              disabled={
                !authStore.can(ADMIN_PERMISSIONS.invitesWrite) ||
                inviteAdminStore.busy
              }
              checked={enabled}
              onChange={setEnabled}
            />
          </label>
          {authStore.can(ADMIN_PERMISSIONS.invitesWrite) && (
            <Button
              disabled={inviteAdminStore.busy}
              onClick={async () => {
                try {
                  await inviteAdminStore.savePolicy({
                    day,
                    week,
                    month,
                    total: policy?.total ?? 0,
                    enabled,
                  });
                  message.success('个人发码额度已保存');
                } catch {
                  message.error('保存额度失败，请重试');
                }
              }}
            >
              保存额度
            </Button>
          )}
        </div>
      </div>
      <div>
        <h2 className="font-medium mb-3">渠道发码统计</h2>
        <div className="flex flex-wrap gap-3">
          {Object.entries(statistics?.byChannel ?? {}).map(
            ([channel, count]) => (
              <span key={channel} className="rounded border px-3 py-1">
                {channel || '直接申请'}：{count}
              </span>
            ),
          )}
        </div>
      </div>
      <div>
        <h2 className="font-medium mb-3">即时邀请码申请</h2>
        <DataTable
          columns={applications}
          data={inviteAdminStore.applications}
          emptyMessage="暂无即时领码申请"
        />
        {inviteAdminStore.nextApplicationId && (
          <Button
            className="mt-3"
            disabled={inviteAdminStore.loading}
            onClick={() => {
              void inviteAdminStore
                .loadMoreApplications()
                .catch(() => message.error('加载申请失败，请重试'));
            }}
          >
            加载更多申请
          </Button>
        )}
      </div>
      <div>
        <h2 className="font-medium mb-3">当前同意回访</h2>
        <p className="text-sm text-muted-foreground mb-3">
          仅返回仍保留同意及联系方式的申请。刷新后读取最新记录；删除回访信息不影响邀请码。
        </p>
        <DataTable
          columns={followups}
          data={inviteAdminStore.followups}
          emptyMessage="暂无同意回访记录"
        />
        {inviteAdminStore.hasMoreFollowups && (
          <Button
            className="mt-3"
            disabled={inviteAdminStore.loading}
            onClick={() => {
              void inviteAdminStore
                .loadMoreFollowups()
                .catch(() => message.error('加载回访记录失败，请重试'));
            }}
          >
            加载更多回访
          </Button>
        )}
      </div>
    </div>
  );
});
